import {
  ANIMATION_SCENE_SPEC,
  type AnimationBlock,
  HOTSPOT_SCENE_VERSIONS,
  type HotspotSceneBlock,
  type LocaleCode,
  PROFILES,
} from '@fixit/bozp-content';
import { BlockRenderer } from '@fixit/bozp-renderer';
import { createRoute } from '@tanstack/react-router';
import { Route as rootRoute } from './__root';

/**
 * Dev-only catalog of every self-drawn scene (21 animation scenes + 3 hotspot
 * arts) exactly as the player renders them, one card per drawing, without
 * walking the dwell-gated chapters of twenty trainings. Registered in
 * router.ts under `import.meta.env.DEV` only — a production bundle has no
 * route here. Review/capture recipe: `?theme=dark` (main.tsx) for the dark
 * palette, `?locale=en-US` for the English microcopy, reduced motion in the
 * browser to start every frame paused, then the rail pills step the art.
 */
export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gallery',
  validateSearch: (search: Record<string, unknown>): { locale?: LocaleCode } => ({
    locale: search.locale === 'en-US' ? 'en-US' : undefined,
  }),
  component: GalleryPage,
});

type Drawing = {
  profile: string;
  block: AnimationBlock | HotspotSceneBlock;
  version: number;
};

function collectDrawings(): Drawing[] {
  const out: Drawing[] = [];
  for (const [slug, profile] of Object.entries(PROFILES)) {
    for (const chapter of profile.content.chapters) {
      for (const block of chapter.blocks) {
        if (block.blockType === 'animation') {
          out.push({ profile: slug, block, version: ANIMATION_SCENE_SPEC[block.scene].version });
        } else if (block.blockType === 'hotspotScene') {
          out.push({ profile: slug, block, version: HOTSPOT_SCENE_VERSIONS[block.scene] });
        }
      }
    }
  }
  return out;
}

function GalleryPage() {
  const { locale = 'cs-CZ' } = Route.useSearch();
  const drawings = collectDrawings();
  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8">
      <h1 className="font-display text-2xl">BOZP scene gallery</h1>
      <p className="mt-1 text-ink-soft">
        {drawings.length} drawings · {locale} · dev only
      </p>
      <div className="mt-8 flex flex-col gap-10">
        {drawings.map(({ profile, block, version }) => (
          <section
            key={block.id}
            id={block.scene}
            data-gallery-scene={block.scene}
            data-gallery-profile={profile}
            className="hb-prose"
          >
            <p className="mb-2 font-mono text-xs text-ink-faint">
              {block.scene} · v{version} · {profile} · {block.blockType}
            </p>
            <BlockRenderer block={block} locale={locale} />
          </section>
        ))}
      </div>
    </main>
  );
}
